import { yupResolver } from '@hookform/resolvers/yup';
import Router from 'next/router';
import { memo, useCallback, useId, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { type InferType, number, object } from 'yup';

import { type OpenCreditPackModalLocations, useTrackCreditPackPurchaseInitiated } from '@air/analytics';
import { Button } from '@air/primitive-button';
import { Label } from '@air/primitive-label';
import { RadioGroup, RadioGroupItem } from '@air/primitive-radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@air/primitive-select';
import { useAirModal, useModalContext } from '@air/provider-modal';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { ManageCreditPacksConfirmationModal } from '~/components/Modals/ManageCreditPacks/ManageCreditPacksConfirmationModal';
import { Routes } from '~/constants/routes';
import { QueryParamNames } from '~/constants/search';
import { useCreditAnalyticsParams } from '~/hooks/useCreditAnalyticsParams';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { useSubscriptionPlan } from '~/swr-hooks/subscriptions/useSubscriptionPlan';
import { useUpdateCreditPacks } from '~/swr-hooks/subscriptions/useUpdateCreditPacks';
import { useCreditStats } from '~/swr-hooks/workspaces/useCreditStats';
import { formatCreditCount, isEligibleForCreditPacks } from '~/utils/creditUtils';
import { formatPricePerCycleDelta } from '~/utils/currencyFormatters';
import { getSubscriptionInfo } from '~/utils/SubscriptionUtils';

const DEFAULT_DROPDOWN_QUANTITY = 10;

// biome-ignore lint/style/noMagicNumbers: product-defined picker shortcuts; remaining quantities are covered by the dropdown
const FIXED_QUANTITIES = [1, 5, 7];

const isFixedQuantity = (quantity: number) => FIXED_QUANTITIES.includes(quantity);

const getDropdownQuantities = (maxCreditPacks: number) =>
  Array.from({ length: maxCreditPacks }, (_, i) => i + 1).filter((q) => !isFixedQuantity(q));

export const UpdateCreditPacksFormValidationSchema = object({
  quantity: number().required().integer(),
});

export type UpdateCreditPacksFormInput = InferType<typeof UpdateCreditPacksFormValidationSchema>;

export type UpdateCreditPacksFormProps = {
  /** Quantity to pre-select */
  initialSelectedQuantity: number;
  /** Surface that opened the manage credit packs flow. Pass `undefined` explicitly when there is
   *  no attributable surface. */
  source: OpenCreditPackModalLocations | undefined;
  /** Optional override for the upgrade branch. When provided, replaces the default navigation-to-checkout. */
  onUpgradeConfirmed?: (quantity: number) => void;
  onClose: () => void;
};

export const UpdateCreditPacksForm = memo(
  ({ initialSelectedQuantity, source, onUpgradeConfirmed, onClose }: UpdateCreditPacksFormProps) => {
    const formId = useId();
    const [showConfirmationModal] = useAirModal(ManageCreditPacksConfirmationModal);
    const { dispatch: modalDispatch } = useModalContext();
    const { data: subscription } = useSubscriptionContext();
    const { data: creditStats } = useCreditStats();
    const { data: subscriptionPlan } = useSubscriptionPlan();
    const { currentWorkspace } = useCurrentWorkspace();
    const { mutateAsync: updateCreditPacks } = useUpdateCreditPacks();
    const { showToast } = useToasts();
    const { trackCreditPackPurchaseInitiated } = useTrackCreditPackPurchaseInitiated();
    const creditAnalyticsParams = useCreditAnalyticsParams();

    const subscriptionInfo = getSubscriptionInfo({
      subscription,
      plan: subscriptionPlan?.plan,
      totalBillableSeats: 0,
    });
    const creditPack = subscriptionInfo?.creditPack;
    const canPurchasePacks = isEligibleForCreditPacks(subscription, creditStats);
    const selectableMax = canPurchasePacks ? (creditPack?.maxQuantity ?? 0) : (creditPack?.quantity ?? 0);

    const dropdownOptions = useMemo(() => getDropdownQuantities(selectableMax), [selectableMax]);

    const [dropdownQuantity, setDropdownQuantity] = useState(() => {
      if (!isFixedQuantity(initialSelectedQuantity)) {
        return initialSelectedQuantity;
      }
      if (dropdownOptions.includes(DEFAULT_DROPDOWN_QUANTITY)) {
        return DEFAULT_DROPDOWN_QUANTITY;
      }

      return dropdownOptions[dropdownOptions.length - 1] ?? initialSelectedQuantity;
    });

    const {
      control,
      formState: { isSubmitting },
      handleSubmit,
      setValue,
    } = useForm<UpdateCreditPacksFormInput>({
      mode: 'all',
      resolver: yupResolver(UpdateCreditPacksFormValidationSchema),
      defaultValues: { quantity: initialSelectedQuantity },
    });

    const selectedQuantity = useWatch({ control, name: 'quantity' });

    const fixedQuantities = useMemo(() => FIXED_QUANTITIES.filter((q) => q <= selectableMax), [selectableMax]);

    const onSubmit = useCallback(
      async ({ quantity }: UpdateCreditPacksFormInput) => {
        if (!creditPack || !subscription) {
          return;
        }

        if (quantity > creditPack.quantity && !isEligibleForCreditPacks(subscription, creditStats)) {
          return;
        }

        // Revert pending downgrade — user picked their current pack count while a pending change
        // is queued. Submit the current quantity directly to supersede the pending change; no
        // checkout (no charge) and no confirmation modal (its copy doesn't fit).
        if (quantity === creditPack.quantity && subscription.pendingPackChange) {
          if (!currentWorkspace?.id) {
            return;
          }

          try {
            await updateCreditPacks({ workspaceId: currentWorkspace.id, quantity });
            showToast('Pending credit pack change canceled.');
            onClose();
          } catch (error) {
            reportErrorToBugsnag({
              error,
              context: 'Failed to cancel pending credit pack change',
              metadata: { data: { workspaceId: currentWorkspace.id, quantity, plan: subscription.plan } },
            });
            showToast('Failed to cancel pending change. Please try again.');
          }
          return;
        }

        if (quantity > creditPack.quantity) {
          if (onUpgradeConfirmed) {
            onUpgradeConfirmed(quantity);
          } else {
            if (creditAnalyticsParams) {
              // `'checkout'` source is structurally unreachable here — checkout always passes
              // `onUpgradeConfirmed`, which short-circuits above. Narrowed for type alignment.
              trackCreditPackPurchaseInitiated({
                ...creditAnalyticsParams,
                source: source === 'checkout' ? undefined : source,
                pack_quantity: quantity,
              });
            }
            // Dismiss any stacked modals (e.g. plans modal opened above this one) before the
            // route change so they don't leak onto the checkout page.
            modalDispatch({ type: 'hide-all' });
            Router.push({
              pathname: Routes.workspaceAdmin.checkout,
              query: { [QueryParamNames.creditPackQuantity]: String(quantity) },
            });
          }

          onClose();
          return;
        }

        // Downgrade — close this modal and surface the confirmation modal in its place. The
        // confirmation owns the mutation + analytics + redirect, and re-opens this modal with the
        // user's selection preserved if they cancel.
        showConfirmationModal({
          source,
          targetQuantity: quantity,
          showUpdateCreditPacksModalOnCancel: true,
          onUpgradeConfirmed,
        });
        onClose();
      },
      [
        creditAnalyticsParams,
        creditPack,
        creditStats,
        currentWorkspace?.id,
        modalDispatch,
        onClose,
        onUpgradeConfirmed,
        showConfirmationModal,
        showToast,
        source,
        subscription,
        trackCreditPackPurchaseInitiated,
        updateCreditPacks,
      ],
    );

    if (!subscriptionInfo?.creditPack || !subscriptionInfo.billingCycle) {
      return null;
    }

    const { billingCycle } = subscriptionInfo;
    const { quantity: currentPackQuantity, size: packSize, pricePerPackCents: packPrice } = subscriptionInfo.creditPack;

    const isSameAsCurrent = selectedQuantity === currentPackQuantity;
    const isRevertingPendingChange = isSameAsCurrent && !!subscription?.pendingPackChange;
    const ctaDisabled =
      isSubmitting || selectedQuantity > selectableMax || (isSameAsCurrent && !isRevertingPendingChange);

    let ctaLabel = 'Add credit pack';

    if (isRevertingPendingChange) {
      ctaLabel = 'Cancel pending downgrade';
    } else if (currentPackQuantity > 0) {
      ctaLabel = 'Update credit pack';
    }

    return (
      <form id={formId} onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <div>
          <p
            id={`${formId}-quantity-label`}
            className="mb-2 font-medium ads:text-primary text-10 text-gray-12 uppercase tracking-wider"
          >
            Plus credit pack
          </p>
          <Controller
            control={control}
            name="quantity"
            // biome-ignore lint/performance/noJsxPropsBind: Controller's render prop receives the field argument
            render={({ field }) => (
              <RadioGroup
                aria-labelledby={`${formId}-quantity-label`}
                value={String(field.value)}
                // biome-ignore lint/performance/noJsxPropsBind: converts the radio value with this field's onChange
                onValueChange={(value) => field.onChange(Number(value))}
                className="gap-2"
                data-testid="MANAGE_CREDIT_PACKS_OPTIONS"
              >
                {fixedQuantities.map((quantity) => (
                  <RadioGroupItem key={quantity} id={`${formId}-quantity-${quantity}`} value={String(quantity)}>
                    <div className="mr-3 ml-2 flex grow items-center justify-between gap-4 font-medium ads:text-primary text-14 text-gray-12">
                      <span>{formatCreditCount(quantity * packSize)} credits/mo</span>
                      {quantity !== currentPackQuantity && (
                        <span>
                          {formatPricePerCycleDelta((quantity - currentPackQuantity) * packPrice, billingCycle)}
                        </span>
                      )}
                    </div>
                  </RadioGroupItem>
                ))}

                {dropdownOptions.length > 0 && (
                  <RadioGroupItem id={`${formId}-quantity-dropdown`} value={String(dropdownQuantity)} className="-mt-1">
                    <div className="mr-3 flex grow items-center justify-between gap-4">
                      <Label htmlFor={`${formId}-quantity-dropdown-select`} className="sr-only">
                        Custom credit pack quantity
                      </Label>
                      <Select
                        value={String(dropdownQuantity)}
                        // biome-ignore lint/performance/noJsxPropsBind: updates both the dropdown state and the form value
                        onValueChange={(value) => {
                          const next = Number(value);
                          setDropdownQuantity(next);
                          setValue('quantity', next, { shouldValidate: true });
                        }}
                      >
                        <SelectTrigger
                          id={`${formId}-quantity-dropdown-select`}
                          className="min-w-[140px]"
                          size="medium"
                        />
                        <SelectContent>
                          {dropdownOptions.map((quantity) => (
                            <SelectItem key={quantity} value={String(quantity)}>
                              {formatCreditCount(quantity * packSize)} credits/mo
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {dropdownQuantity !== currentPackQuantity && (
                        <span className="font-medium ads:text-primary text-14 text-gray-12">
                          {formatPricePerCycleDelta((dropdownQuantity - currentPackQuantity) * packPrice, billingCycle)}
                        </span>
                      )}
                    </div>
                  </RadioGroupItem>
                )}
              </RadioGroup>
            )}
          />
        </div>

        <footer className="flex items-center justify-end gap-2">
          <Button type="button" appearance="outline" color="gray" onClick={onClose} size="medium">
            Cancel
          </Button>
          <Button
            type="submit"
            appearance="filled"
            color="blue"
            disabled={ctaDisabled}
            isLoading={isSubmitting}
            size="medium"
            data-testid="MANAGE_CREDIT_PACKS_CTA"
          >
            {ctaLabel}
          </Button>
        </footer>
      </form>
    );
  },
);

UpdateCreditPacksForm.displayName = 'UpdateCreditPacksForm';
